import { zodResolver } from "@hookform/resolvers/zod";
import { useTheme } from "@mui/material";
import { UploadIcon } from "lucide-react";
import type { FC } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useDocumentUploadMutation } from "web_myplann/Api/Document/useDocumentUploadMutation";
import { usePortfolioRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { isNetworkFailure } from "web_myplann/Infrastructure/Api/ApiUtils";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DocumentFileFormField } from "web_myplann/Portfolio/Forms/DocumentUploadForm/DocumentFileFormField";
import { DocumentUploadFormFields } from "web_myplann/Portfolio/Forms/DocumentUploadForm/DocumentUploadFormFields";
import {
	type DocumentUploadFormInput,
	type DocumentUploadFormOutput,
	getDocumentUploadFormSchema,
	type PickedFile,
} from "web_myplann/Portfolio/Forms/DocumentUploadForm/documentUploadFormSchema";
import { getAutofilledDocumentName } from "web_myplann/Portfolio/Forms/DocumentUploadForm/getAutofilledDocumentName";
import { mapDocumentUploadFormToRequest } from "web_myplann/Portfolio/Forms/DocumentUploadForm/mapDocumentUploadFormToRequest";
import { mapOriginToDocumentUploadForm } from "web_myplann/Portfolio/Forms/DocumentUploadForm/mapOriginToDocumentUploadForm";
import type { DocumentUploadOrigin } from "web_myplann/Portfolio/Helpers/documentUploadOrigin";
import { PortfolioFormShell } from "web_myplann/Portfolio/PortfolioShared/PortfolioFormShell";
import type { UploadedDocument } from "web_myplann/Portfolio/Types/uploadedDocument";

const R = Resources.Portfolio.List.DocumentUpload;

type Props = {
	origin: DocumentUploadOrigin;
	onUploaded: (document: UploadedDocument) => void;
};

export const UploadDocumentForm: FC<Props> = ({ origin, onUploaded }) => {
	const { iconSize } = useTheme();
	const { t } = useResource();
	const { hasRight: hasPortfolioRight } = usePortfolioRightOptional();
	const showContractField = origin.link === null && hasPortfolioRight;

	const form = useForm<
		DocumentUploadFormInput,
		unknown,
		DocumentUploadFormOutput
	>({
		resolver: zodResolver(getDocumentUploadFormSchema(t)),
		defaultValues: mapOriginToDocumentUploadForm(origin),
		mode: "onChange",
	});
	const { mutate: uploadDocument, isPending } = useDocumentUploadMutation();

	const handleFileChange = (
		nextFile: PickedFile | null,
		previousFile: PickedFile | null,
	) => {
		const name = getAutofilledDocumentName({
			currentName: form.getValues("name"),
			previousFile,
			nextFile,
		});
		form.setValue("name", name, { shouldValidate: true });
	};

	const handleSubmit = form.handleSubmit((values) => {
		const request = mapDocumentUploadFormToRequest(values, origin);

		uploadDocument(
			{ data: request },
			{
				onSuccess: () =>
					onUploaded({
						name: request.title,
						folder: values.folder,
						linkedItemTitle: values.linkedItem?.title ?? null,
					}),
				onError: (error) => {
					if (isNetworkFailure(error)) {
						toast.error(t(R.uploadError));
					}
				},
			},
		);
	});

	return (
		<PortfolioFormShell
			onSubmit={handleSubmit}
			submitLabel={t(R.submit)}
			submitIcon={<UploadIcon size={iconSize.md} />}
			disabled={!form.formState.isValid}
			loading={isPending}
		>
			<DocumentFileFormField
				control={form.control}
				name="pickedFile"
				disabled={isPending}
				onValueChange={handleFileChange}
			/>
			<DocumentUploadFormFields
				control={form.control}
				showContractField={showContractField}
			/>
		</PortfolioFormShell>
	);
};
